import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  getSerpIntermediaryInspectionRows,
  getSerpIntermediarySettlementRows,
  type SerpIntermediaryReplaceSource,
} from '@/utils/approval/serpIntermediaryReplace';
import { SerpIntermediaryReplaceRoot } from './styles';

const TITLE_BG = '#fff2cc';
const LABEL_BG = '#f3f3f3';

const bg = (color: string) => ({ backgroundColor: color });

type TablesProps = {
  source: SerpIntermediaryReplaceSource;
  t: TFunction;
};

type ManagerRowsProps = TablesProps & {
  label: string;
  prefix: 'BEF' | 'AFT';
};

/** 인계자·인수자 — 비고 줄은 레거시도 입력칸·데이터 없는 빈칸 */
const ManagerRows = ({ source, t, label, prefix }: ManagerRowsProps) => (
  <>
    <tr>
      <th style={bg(LABEL_BG)} rowSpan={4}>
        {label}
      </th>
      <th style={bg(LABEL_BG)}>{t('코드')}</th>
      <th style={bg(LABEL_BG)}>{t('성명')}</th>
      <th style={bg(LABEL_BG)}>{t('근무기간')}</th>
      <th style={bg(LABEL_BG)}>{t('사업자번호')}</th>
    </tr>
    <tr>
      <td>{formatSerpText(source[`${prefix}_SM_SHOP_CD`])}</td>
      <td>{formatSerpText(source[`${prefix}_SM_REP_NM`])}</td>
      <td>
        {`${formatSerpText(source[`${prefix}_WORK_STA_DE`])} - ${formatSerpText(
          source[`${prefix}_WORK_END_DE`],
        )}`}
      </td>
      <td>{formatSerpText(source[`${prefix}_SM_BRN`])}</td>
    </tr>
    <tr>
      <th style={bg(LABEL_BG)} colSpan={4}>
        {t('비고')}
      </th>
    </tr>
    <tr>
      <td colSpan={4}>{' '}</td>
    </tr>
  </>
);

const TotalRow = ({ label, value }: { label: string; value: unknown }) => (
  <tr>
    <th style={bg(LABEL_BG)} colSpan={2}>
      {label}
    </th>
    <td />
    <td />
    <td />
    <td>{formatSerpText(value)}</td>
    <td />
  </tr>
);

/** 레거시 #APV_SERP_SHOP_CHANGE_MANAGER .form-group 의 제목·표 4개(칸 구성·색 동일, 금액·비율은 응답 글자 그대로) */
const SerpIntermediaryReplaceTables = ({ source, t }: TablesProps) => {
  const inspections = getSerpIntermediaryInspectionRows(source);
  const settlements = getSerpIntermediarySettlementRows(source);

  return (
    <>
      <h2 className="heading-md pt-20 pb-10">{t('중간관리자 교체 정산')}</h2>
      <table className="table table-orders table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(LABEL_BG)} colSpan={3}>
              {t('브랜드')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('매장정보')}
            </th>
          </tr>
          <tr>
            <td colSpan={3}>{formatSerpText(source.BRAND_NM)}</td>
            <td colSpan={2}>{formatSerpText(source.SHOP_NM)}</td>
          </tr>
          <ManagerRows source={source} t={t} label={t('인계자')} prefix="BEF" />
          <ManagerRows source={source} t={t} label={t('인수자')} prefix="AFT" />
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <thead>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={8}>
              {t('매장실사 내용')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('시즌')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('전산')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('실사')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('차이')}
            </th>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('Loss 금액')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
          </tr>
        </thead>
        <tbody>
          {inspections.map((row, index) => (
            <tr key={`inspection-${index}`}>
              <th style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</th>
              <td>{formatSerpText(row.STD_STK_QTY)}</td>
              <td>{formatSerpText(row.SALE_AMT)}</td>
              <td>{formatSerpText(row.STCKT_QTY)}</td>
              <td>{formatSerpText(row.STCKT_AMT)}</td>
              <td>{formatSerpText(row.GAP_QTY)}</td>
              <td>{formatSerpText(row.GAP_AMT)}</td>
              <td>{formatSerpText(row.LOSS_AMT)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <thead>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={7}>
              {t('정산내역')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('구분')}
            </th>
            <th style={bg(LABEL_BG)}>{t('Loss 금액')}</th>
            <th style={bg(LABEL_BG)}>{t('공급가율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('공제비율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('정산 금액')}</th>
            <th style={bg(LABEL_BG)}>{t('비고')}</th>
          </tr>
        </thead>
        <tbody>
          {settlements.map((row, index) => (
            <tr key={`settlement-${index}`}>
              {index === 0 && (
                <th style={bg(LABEL_BG)} rowSpan={settlements.length}>
                  Loss
                </th>
              )}
              <th style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</th>
              <td>{formatSerpText(row.LOSS_AMT)}</td>
              <td>{formatSerpText(row.GI_RAT)}</td>
              <td>{formatSerpText(row.DED_RTO)}</td>
              <td>{formatSerpText(row.STMT_AMT)}</td>
              <td>{formatSerpText(row.APRV_RMK)}</td>
            </tr>
          ))}
          <TotalRow label={t('미처리금액')} value={source.EXC_AMT} />
          <TotalRow label={t('합계')} value={source.TOTAL_STMT_AMT} />
          <TotalRow label={t('거래 보증금')} value={source.TRAD_GRT_AMT} />
          <TotalRow label={t('보험금액')} value={source.GRT_INSUR_AMT} />
        </tbody>
      </table>
      {/* 레거시도 입력칸·데이터 없는 빈칸 */}
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={2}>
              {t('정산내역')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('인계자')}</th>
            <td>{' '}</td>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('인수자')}</th>
            <td>{' '}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SerpIntermediaryReplaceTablesView = (props: TablesProps) => (
  <SerpIntermediaryReplaceRoot>
    <div className="form-group">
      <SerpIntermediaryReplaceTables {...props} />
    </div>
  </SerpIntermediaryReplaceRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpIntermediaryReplaceContentHtml = (
  source: SerpIntermediaryReplaceSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpIntermediaryReplaceTables source={source} t={t} />
    </div>,
  );

/** 저장 content(레거시 문서 포함) 그대로 */
export const SerpIntermediaryReplaceSnapshot = ({
  content,
}: {
  content: string;
}) => (
  <SerpIntermediaryReplaceRoot>
    <SerpContentSnapshot content={content} />
  </SerpIntermediaryReplaceRoot>
);
